import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { PodcastCard } from "@/components/PodcastCard";
import { NewsletterModal } from "@/components/NewsletterModal";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";

const TODAY_LABEL = new Date().toLocaleDateString("bg-BG", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function SectionHeading({ children, accent = "copper", testId }) {
  const underline = accent === "navy" ? "border-[#10243A]" : "border-[#C47A35]";
  return (
    <h2 className={`font-sans text-xl sm:text-[22px] font-bold tracking-tight text-[#10243A] mb-6 pb-3 border-b-2 ${underline} inline-block uppercase`} data-testid={testId}>
      {children}
    </h2>
  );
}

export default function HomePage() {
  const [data, setData] = useState(null);
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  useEffect(() => {
    api.get("/homepage").then(({ data }) => setData(data));
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("metri_newsletter_shown")) return;
    const timer = setTimeout(() => {
      setNewsletterOpen(true);
      sessionStorage.setItem("metri_newsletter_shown", "1");
    }, 15000);
    return () => clearTimeout(timer);
  }, []);

  if (!data) return <div className="p-24 text-center text-[#66717B]">Зареждане...</div>;

  return (
    <div data-testid="homepage">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SEO title="Metri.bg — Медия за имоти, инвестиции и икономика" description="Новини и анализи за имотния пазар, инвестициите, ипотеките и икономиката в България и Европа." />

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 py-10 border-b border-[#E4E7E9]">
        <div className="lg:col-span-8">
          {data.lead && <ArticleCard article={data.lead} variant="hero" />}
        </div>
        <div className="lg:col-span-4" data-testid="homepage-secondary-leads-wrapper">
          <div className="text-right text-xs font-semibold uppercase tracking-wide text-[#66717B] mb-4" data-testid="homepage-today-date">
            {TODAY_LABEL}
          </div>
          <div className="space-y-6 lg:border-l lg:border-[#E4E7E9] lg:pl-8" data-testid="homepage-secondary-leads">
            {data.secondary.map((a) => <ArticleCard key={a.id} article={a} variant="secondary" />)}
          </div>
        </div>
      </section>

      <section className="py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <SectionHeading accent="navy" testId="section-title-latest">Последни</SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="homepage-latest">
            {data.latest.map((a) => <ArticleCard key={a.id} article={a} variant="grid" />)}
          </div>
        </div>
        <div className="space-y-10">
          <div>
            <SectionHeading testId="section-title-most-read">Най-четени</SectionHeading>
            <ol className="space-y-4" data-testid="homepage-most-read">
              {data.most_read.map((a, i) => (
                <li key={a.id} className="flex gap-4">
                  <span className="font-serif text-3xl font-bold text-[#C47A35]/60">{i + 1}</span>
                  <Link to={`/article/${a.slug}`} className="font-serif text-base font-semibold text-[#10243A] hover:text-[#C47A35] leading-snug pt-1 transition-colors">
                    {a.title}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <SectionHeading accent="navy" testId="section-title-most-commented">Най-коментирани</SectionHeading>
            <ol className="space-y-4" data-testid="homepage-most-commented">
              {data.most_commented.map((a, i) => (
                <li key={a.id} className="flex gap-4">
                  <span className="font-serif text-3xl font-bold text-[#10243A]/45">{i + 1}</span>
                  <Link to={`/article/${a.slug}`} className="font-serif text-base font-semibold text-[#10243A] hover:text-[#C47A35] leading-snug pt-1 transition-colors">
                    {a.title}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {data.podcasts.length > 0 && (
        <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-[#10243A] px-4 sm:px-6 lg:px-8 py-14" data-testid="homepage-podcasts-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-7">
              <h2 className="font-sans text-xl sm:text-[22px] font-bold tracking-tight text-white pb-3 border-b-2 border-[#C47A35] inline-block uppercase">
                Подкаст
              </h2>
              <Link to="/podcasts" className="text-sm font-semibold text-[#C47A35] hover:text-white transition-colors">
                Всички &rarr;
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="homepage-podcasts">
              {data.podcasts.map((p) => <PodcastCard key={p.id} episode={p} dark />)}
            </div>
          </div>
        </section>
      )}

      {data.editors_pick.length > 0 && (
        <section className="py-14 border-t border-[#E4E7E9]">
          <SectionHeading testId="section-title-editors-pick">Препоръчано от редакцията</SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="homepage-editors-pick">
            {data.editors_pick.map((a) => <ArticleCard key={a.id} article={a} variant="compact" />)}
          </div>
        </section>
      )}

      <NewsletterModal open={newsletterOpen} onOpenChange={setNewsletterOpen} source="homepage_popup" />
      </div>
    </div>
  );
}
